/* istanbul ignore file */
/**
 * Created by rathompson on 11/3/21
 * File: PredefinedRolesTable
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { useCallback, useContext, useMemo } from 'react'
import {
  ConnectedTable,
  TableProvider,
  ConnectedTableLayout,
} from '@panwds/react-table'
import {
  useModal
} from '@panwds/react-ui'
import TableHeader from '../../../../common/components/table/TableHeader'
import NameRenderer from './cellRenderer/NameRenderer'
import DescriptionRenderer from './cellRenderer/DescriptionRenderer'
import ViewRoleModal from '../modal/ViewRoleModal'
import InheritedRoleModal from '../modal/InheritedRoleModal'
import {
  LABEL_HEADER_ROLE_DESC,
  LABEL_HEADER_CUSTOM_ROLE_NAME,
  LABEL_PREDEFINED_ROLES_SUBTITLE,
  LABEL_PREDEFINED_ROLES_TITLE,
} from '../../../constants/LabelConstants'
import styled from 'styled-components'
import { FF_V2_ROLE_MODAL } from '../../../../common/utils/FeatureFlags'
import { getVarValue } from '../../../../common/utils/Sparky'
import GlobalContext from '../../../../common/context/GlobalContext'
import V5TableWrapper from '../../../../common/components/table/V5TableWrapper'

const CONTAINER = styled(V5TableWrapper)`
  flex: 1 1;
  height: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  .tw-border-solid.tw-border-gray-200.tw-border-0.tw-flex.tw-relative.tw-bg-white.tw-border.tw-overflow-auto {
    flex: 1 1;
  }
  .tw-font-lato.tw-box-border.tw-px-4.tw-py-1.tw-items-center.tw-flex.tw-h-full.tw-w-full.tw-leading-tight {
    align-items: baseline;
  }
  .tw-flex.tw-bg-white {
    flex: 1 1;
  }
`

const useV2RoleModal = getVarValue(FF_V2_ROLE_MODAL)

/**
 *   Predefined table list
 */
const PredefinedRolesTable = () => {

  const { iamReducer } = useContext(GlobalContext)
  const { roles, permissionsMap } = iamReducer
  /**
   *     ========= Local State ===========
   */
  const viewRoleModal = useModal(useV2RoleModal ? ViewRoleModal : InheritedRoleModal as any)

  /**
   *     ========= Callback ===========
   */
  const onNameClick = useCallback(({ row }) => {
    viewRoleModal.openModal({ role: row?.original, permissionsMap })
  }, [viewRoleModal, permissionsMap])

  const columns = useMemo(() => [
    {
      accessor: (row: any) => row.label,
      Header: LABEL_HEADER_CUSTOM_ROLE_NAME,
      Cell: (props: any) => <NameRenderer { ...props } onClick={onNameClick.bind(this, props)} />,
      width: 232,
      id: 'label',
    },
    {
      accessor: (row: any) => row.description,
      Header: LABEL_HEADER_ROLE_DESC,
      Cell: DescriptionRenderer,
      width: 232,
      id: 'description',
    }
  ], [onNameClick])

  return (
    <CONTAINER>
      <TableProvider
        columns={columns}
        data={roles}
      >
        <TableHeader title={LABEL_PREDEFINED_ROLES_TITLE} subtitle={LABEL_PREDEFINED_ROLES_SUBTITLE}/>
        <ConnectedTableLayout
          sidecar={<div/>}
          overflow
          resultsSummary={false}
        >
          <ConnectedTable
            showFooter={false}
          />
        </ConnectedTableLayout>
      </TableProvider>
    </CONTAINER>
  )
}

export default PredefinedRolesTable
